import type { DeleteQuestionnaireTemplateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the TRPC delete response to match the GraphQL mutation structure.
 *
 * The GraphQL `deleteQuestionnaireTemplate` mutation reported an affected-row
 * count for the version cascade (`delete_questionnaire_template_version`) plus
 * the deleted row (`delete_questionnaire_template_by_pk`). The tRPC backend is
 * a single delete returning void; the version cascade happens server-side. We
 * surface a single deleted template (id-based API) and a zero version count,
 * matching the legacy shape consumers branch on.
 */
const mapTrpcResponseToGraphQL = (
  id: string
): DeleteQuestionnaireTemplateMutation => {
  return {
    delete_questionnaire_template_version: {
      __typename: 'questionnaire_template_version_mutation_response',
      affected_rows: 0,
    },
    delete_questionnaire_template_by_pk: {
      __typename: 'questionnaire_template',
      Id: id,
    },
  };
};

export const useDeleteQuestionnaireTemplateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.questionnaireTemplate.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplate.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireTemplate.getById.queryKey(),
        });
      },
    }),
  });

  return {
    deleteQuestionnaireTemplate: async (
      id: string
    ): Promise<DeleteQuestionnaireTemplateMutation> => {
      await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL(id);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
